// frontend/src/components/DirectChat.js
// مكون المحادثة المباشرة مع الأصدقاء
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { ArrowRight, Send, User, Check, CheckCheck, Clock, AlertTriangle, UserX, UserCheck, Smile, Trash2, Paperclip, Mic, CornerUpLeft, Play, Pause, Camera, Square, Lock, X } from 'lucide-react';
import './DirectChat.css';
import { useChat } from '../context/ChatContext';
import { VipName } from './VipName';
import useTelegramBackButton from '../hooks/useTelegramBackButton';
import ReportModal from './ReportModal';

const truncateText = (text, maxLength = 80) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const DirectChat = ({ user, friend, onBack }) => {
  const { socket } = useChat();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [partnerOnline, setPartnerOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isBlockedByMe, setIsBlockedByMe] = useState(friend?.isBlocked || false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const hasAppliedInitialScrollRef = useRef(false);
  const previousMessageCountRef = useRef(0);
  const typingTimeoutRef = useRef(null);
  const [isInitialScrollReady, setIsInitialScrollReady] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [oldestTimestamp, setOldestTimestamp] = useState(null);

  // Redesign state additions
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [activeOptionMenuMessageId, setActiveOptionMenuMessageId] = useState(null);

  const previewAudioRef = useRef(null);
  const touchStartX = useRef(0);
  const swipedMessageRef = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipedMessageId, setSwipedMessageId] = useState(null);

  // Media states & refs
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const photoInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const userId = user?.id || user?.telegramId;
  const friendId = friend?.id;

  const handlePhotoClick = () => {
    if (!user?.isVip) {
      alert("هذه الميزة مخصصة لمشتركي VIP فقط! 🌟");
      return;
    }
    photoInputRef.current.click();
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("الحد الأقصى لحجم الصورة هو 10 ميجابايت.");
      return;
    }

    setUploadingMedia(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        sendDirectMediaMessage(data.url, 'image');
      } else {
        alert("فشل رفع الصورة");
      }
    } catch (err) {
      console.error(err);
      alert("حدث خطأ أثناء رفع الصورة");
    } finally {
      setUploadingMedia(false);
      e.target.value = ''; // Reset input
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startVoiceRecording = async () => {
    if (!user?.isVip) {
      alert("هذه الميزة مخصصة لمشتركي VIP فقط! 🌟");
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("متصفحك لا يدعم تسجيل الصوت.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());

        if (audioChunksRef.current.length === 0) return;

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedAudioBlob(audioBlob);
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudioUrl(audioUrl);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic access error:', err);
      alert("لا يمكن الوصول للميكروفون. يرجى تفعيل الصلاحية.");
    }
  };

  const stopVoiceRecording = (shouldSend = true) => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return;

    clearInterval(recordingTimerRef.current);
    setIsRecording(false);

    if (shouldSend) {
      mediaRecorderRef.current.stop();
    } else {
      const stream = mediaRecorderRef.current.stream;
      stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
    }
  };

  const discardVoicePreview = () => {
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
    }
    setRecordedAudioUrl(null);
    setRecordedAudioBlob(null);
    setIsPlayingPreview(false);
  };

  const sendVoicePreview = async () => {
    if (!recordedAudioBlob) return;

    setUploadingMedia(true);
    const audioFile = new File([recordedAudioBlob], 'voice-recording.webm', { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', audioFile);

    try {
      const res = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        sendDirectMediaMessage(data.url, 'audio');
      } else {
        alert("فشل رفع التسجيل الصوتي");
      }
    } catch (err) {
      console.error(err);
      alert("حدث خطأ أثناء رفع التسجيل الصوتي");
    } finally {
      setUploadingMedia(false);
      discardVoicePreview();
    }
  };

  const togglePlayPreview = () => {
    if (!previewAudioRef.current) return;
    if (isPlayingPreview) {
      previewAudioRef.current.pause();
      setIsPlayingPreview(false);
    } else {
      previewAudioRef.current.play();
      setIsPlayingPreview(true);
    }
  };

  const sendDirectMediaMessage = (url, type) => {
    if (!socket) return;
    const msg = {
      id: Date.now(),
      text: url,
      mediaType: type,
      sender: 'me',
      replyTo: replyingToMessage,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };

    setMessages(prev => [...prev, msg]);
    socket.emit('direct_chat_message', {
      id: msg.id,
      text: url,
      mediaType: type,
      senderId: userId,
      receiverId: friendId,
      replyTo: replyingToMessage
    });
    setReplyingToMessage(null);
  };

  // Swipe-to-reply touch events
  const handleTouchStart = (e, msg) => {
    touchStartX.current = e.touches[0].clientX;
    swipedMessageRef.current = msg;
    setSwipedMessageId(msg.id || msg.dbId);
  };

  const handleTouchMove = (e) => {
    if (!swipedMessageRef.current) return;
    const currentX = e.touches[0].clientX;
    const diffX = touchStartX.current - currentX;
    if (diffX > 0) {
      const offset = Math.min(diffX, 80);
      setSwipeOffset(offset);
    }
  };

  const handleTouchEnd = () => {
    if (swipedMessageRef.current && swipeOffset >= 50) {
      const msg = swipedMessageRef.current;
      const senderName = msg.sender === 'me' ? 'أنا' : (friend?.alias || friend?.firstName || 'صديقي');
      setReplyingToMessage({
        id: msg.id || msg.dbId,
        text: msg.mediaType === 'image' ? '📷 صورة' : (msg.mediaType === 'audio' ? '🎙️ رسالة صوتية' : msg.text),
        senderName
      });
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
    }
    setSwipeOffset(0);
    swipedMessageRef.current = null;
    setSwipedMessageId(null);
  };

  const handleReplySnippetClick = (replyToId) => {
    const element = document.getElementById(`msg-${replyToId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('message-highlight');
      setTimeout(() => {
        element.classList.remove('message-highlight');
      }, 1500);
    }
  };

  const handleBlock = async () => {
    if (!userId || !friendId) return;
    const confirmBlock = window.confirm('هل أنت متأكد من حظر هذا الصديق؟ لن تتمكن من مراسلته.');
    if (!confirmBlock) return;
    try {
      const res = await fetch(`/api/friends/${userId}/block/${friendId}`, { method: 'POST' });
      if (res.ok) {
        setIsBlockedByMe(true);
      } else {
        alert('حدث خطأ أثناء محاولة الحظر.');
      }
    } catch (err) {
      console.error('Error blocking friend:', err);
    }
  };

  const handleUnblock = async () => {
    if (!userId || !friendId) return;
    try {
      const res = await fetch(`/api/friends/${userId}/unblock/${friendId}`, { method: 'POST' });
      if (res.ok) {
        setIsBlockedByMe(false);
      } else {
        alert('حدث خطأ أثناء محاولة إلغاء الحظر.');
      }
    } catch (err) {
      console.error('Error unblocking friend:', err);
    }
  };

  const emojis = ['😊', '😂', '❤️', '👍', '😍', '🔥', '🎉', '😭', '🤔', '🙌', '👏', '✨', '🚀', '🌟', '💯', '👀', '🌹', '💔', '🎈', '✌️'];

  const handleEmojiClick = (emoji) => {
    setInputText(prev => prev + emoji);
  };

  // التمرير التلقائي
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesAreaRef.current) {
      if (behavior === 'auto') {
        messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
      } else {
        messagesAreaRef.current.scrollTo({
          top: messagesAreaRef.current.scrollHeight,
          behavior
        });
      }
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // جلب سجل المحادثة
  useEffect(() => {
    const fetchHistory = async () => {
      if (!userId || !friendId) return;
      hasAppliedInitialScrollRef.current = false;
      previousMessageCountRef.current = 0;
      setIsInitialScrollReady(false);
      setHasMore(false);
      setOldestTimestamp(null);
      setLoading(true);
      try {
        const res = await fetch(`/api/chats/${userId}/history/${friendId}`);
        if (res.ok) {
          const data = await res.json();
          setMessages(data.history || []);
          setHasMore(data.hasMore || false);
          setOldestTimestamp(data.oldestTimestamp || null);
        }
      } catch (err) {
        console.error('Error fetching direct chat history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [userId, friendId]);

  // تحميل رسائل أقدم (load more)
  const loadMoreMessages = async () => {
    if (!hasMore || loadingMore || !oldestTimestamp) return;
    setLoadingMore(true);
    const scrollArea = messagesAreaRef.current;
    const prevScrollHeight = scrollArea ? scrollArea.scrollHeight : 0;
    try {
      const res = await fetch(`/api/chats/${userId}/history/${friendId}?before=${encodeURIComponent(oldestTimestamp)}`);
      if (res.ok) {
        const data = await res.json();
        const olderMessages = data.history || [];
        if (olderMessages.length > 0) {
          setMessages(prev => [...olderMessages, ...prev]);
          setOldestTimestamp(data.oldestTimestamp || null);
          setHasMore(data.hasMore || false);
          // Maintain scroll position
          requestAnimationFrame(() => {
            if (scrollArea) {
              scrollArea.scrollTop = scrollArea.scrollHeight - prevScrollHeight;
            }
          });
        } else {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('Error loading more messages:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // الانضمام لغرفة الشات المباشر
  useEffect(() => {
    if (!socket || !userId || !friendId) return;

    const joinRoom = () => {
      socket.emit('direct_chat_join', { userId, partnerId: friendId });
    };

    if (socket.connected) {
      joinRoom();
    }

    socket.on('connect', joinRoom);

    const handleReceive = (message) => {
      setMessages(prev => [...prev, message]);
      setIsTyping(false);
      socket.emit('direct_message_read', { id: message.id });
    };

    const handleTyping = ({ userId: typerId }) => {
      if (typerId === friendId) {
        setIsTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
      }
    };

    const handlePartnerOnline = () => setPartnerOnline(true);
    const handlePartnerOffline = () => setPartnerOnline(false);

    const handleMessageStatus = ({ id, dbId, status }) => {
      setMessages(prev => prev.map(msg => {
        if (msg.id === id || msg.dbId === id || (dbId && (msg.id === dbId || msg.dbId === dbId))) {
          return { ...msg, status, ...(dbId ? { dbId } : {}) };
        }
        return msg;
      }));
    };

    const handleMessageDeleted = ({ messageId }) => {
      setMessages(prev => prev.filter(msg => msg.id !== messageId && msg.dbId !== messageId));
    };

    socket.on('direct_chat_receive', handleReceive);
    socket.on('direct_chat_typing', handleTyping);
    socket.on('direct_partner_online', handlePartnerOnline);
    socket.on('direct_partner_offline', handlePartnerOffline);
    socket.on('direct_message_status', handleMessageStatus);
    socket.on('direct_message_deleted', handleMessageDeleted);

    return () => {
      socket.emit('direct_chat_leave');
      socket.off('connect', joinRoom);
      socket.off('direct_chat_receive', handleReceive);
      socket.off('direct_chat_typing', handleTyping);
      socket.off('direct_partner_online', handlePartnerOnline);
      socket.off('direct_partner_offline', handlePartnerOffline);
      socket.off('direct_message_status', handleMessageStatus);
      socket.off('direct_message_deleted', handleMessageDeleted);
    };
  }, [socket, userId, friendId]);

  const handleDeleteMessage = (msgId) => {
    if (!socket || !msgId) return;
    if (window.confirm("هل تريد حذف هذه الرسالة؟ سيتم حذفها لدى الطرف الآخر أيضاً.")) {
      socket.emit('delete_direct_message', { messageId: msgId });
    }
  };

  useLayoutEffect(() => {
    if (loading || hasAppliedInitialScrollRef.current) return;

    scrollToBottom('auto');
    hasAppliedInitialScrollRef.current = true;
    previousMessageCountRef.current = messages.length;
    setIsInitialScrollReady(true);
  }, [loading, messages.length, scrollToBottom]);

  // التمرير عند الرسائل الجديدة فقط بعد تثبيت موضع الفتح الأول.
  useEffect(() => {
    if (!isInitialScrollReady) return;

    if (messages.length > previousMessageCountRef.current || isTyping) {
      scrollToBottom();
    }
    previousMessageCountRef.current = messages.length;
  }, [messages.length, isTyping, isInitialScrollReady, scrollToBottom]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputText.trim() || !socket) return;

    const msg = {
      id: Date.now(),
      text: inputText,
      sender: 'me',
      replyTo: replyingToMessage,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };

    setMessages(prev => [...prev, msg]);
    socket.emit('direct_chat_message', {
      id: msg.id,
      text: inputText,
      senderId: userId,
      receiverId: friendId,
      replyTo: replyingToMessage
    });
    setInputText('');
    setShowEmojiPicker(false);
    setReplyingToMessage(null);
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    if (socket) socket.emit('direct_chat_typing');
  };

  const handleReport = async (reason) => {
    try {
      await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reporterId: userId,
          reportedEntityId: friendId,
          entityType: 'user',
          reason,
          messages: messages.slice(-10)
        })
      });
      alert('تم إرسال البلاغ بنجاح.');
    } catch (e) {
      console.error('Error reporting:', e);
    }
  };

  const renderMessageStatus = (status) => {
    switch (status) {
      case 'sending': return <Clock size={12} className="status-icon" style={{ color: '#999' }} />;
      case 'sent': return <Check size={14} className="status-icon" style={{ color: '#999' }} />;
      case 'delivered': return <CheckCheck size={14} className="status-icon" style={{ color: '#999' }} />;
      case 'read': return <CheckCheck size={14} className="status-icon" style={{ color: '#0088CC' }} />;
      default: return null;
    }
  };

  const isNativeBackActive = useTelegramBackButton(onBack);

  return (
    <div className="direct-chat-container">
      {/* هيدر */}
      <div className="direct-chat-header">
        {!isNativeBackActive && (
          <button className="back-btn-round" onClick={onBack} aria-label="عودة">
            <ArrowRight size={20} />
          </button>
        )}
        <div className="direct-partner-info">
          <div className="direct-partner-avatar">
            {friend?.avatarUrl ? (
              <img src={friend.avatarUrl} alt="avatar" />
            ) : (
              <User size={20} />
            )}
            <span className={`online-dot-small ${partnerOnline || friend?.isOnline ? 'online' : 'offline'}`}></span>
          </div>
          <div className="direct-partner-details">
            <h3>
              <VipName user={{ nickname: friend?.alias || friend?.firstName, isVip: friend?.isVip, customNameColor: friend?.customNameColor }} />
            </h3>
            <p className={`partner-status-text ${isTyping ? 'typing' : ''}`}>
              {isTyping ? 'يكتب الآن...' : (partnerOnline || friend?.isOnline ? 'متصل' : 'غير متصل')}
            </p>
          </div>
        </div>
        <div className="direct-chat-actions">
          {isBlockedByMe ? (
            <button className="action-icon-btn unblock" onClick={handleUnblock} title="إلغاء الحظر">
              <UserCheck size={16} />
            </button>
          ) : (
            <button className="action-icon-btn block" onClick={handleBlock} title="حظر">
              <UserX size={16} />
            </button>
          )}
          <button className="action-icon-btn report" onClick={() => setIsReportModalOpen(true)} title="إبلاغ">
            <AlertTriangle size={16} />
          </button>
        </div>
      </div>

      {/* منطقة الرسائل */}
      <div
        className="direct-messages-area"
        ref={messagesAreaRef}
        style={{
          opacity: loading || !isInitialScrollReady ? 0 : 1,
          scrollBehavior: isInitialScrollReady ? undefined : 'auto'
        }}
      >
        {loading ? (
          <div className="direct-loading">
            <span className="loader-spinner"></span>
          </div>
        ) : messages.length === 0 ? (
          <div className="direct-empty-chat">
            <User size={48} />
            <p>ابدأ المحادثة مع {friend?.alias || friend?.firstName || 'صديقك'}</p>
          </div>
        ) : (
          <>
            {/* زر تحميل الرسائل القديمة */}
            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                <button
                  onClick={loadMoreMessages}
                  disabled={loadingMore}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: loadingMore ? 'default' : 'pointer',
                    background: 'rgba(255,255,255,0.08)', color: 'var(--text-secondary, #aaa)',
                    fontSize: '0.82rem', fontWeight: '600', backdropFilter: 'blur(8px)',
                    transition: 'all 0.2s'
                  }}
                >
                  {loadingMore ? (
                    <><span className="loader-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> جاري التحميل...</>
                  ) : (
                    <>↑ اظهار الرسائل القديمة</>
                  )}
                </button>
              </div>
            )}
          {messages.map((msg, idx) => (
            <div
              key={msg.id || idx}
              id={`msg-${msg.id || msg.dbId}`}
              className={`message-wrapper ${msg.sender === 'me' ? 'sent' : 'received'}`}
              onTouchStart={(e) => handleTouchStart(e, msg)}
              onTouchMove={(e) => handleTouchMove(e)}
              onTouchEnd={handleTouchEnd}
              style={{
                transform: swipedMessageId === (msg.id || msg.dbId) ? `translateX(-${swipeOffset}px)` : 'none',
                transition: swipedMessageId === (msg.id || msg.dbId) && swipeOffset !== 0 ? 'none' : 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
              }}
            >
              <div 
                className="message-bubble direct-message-bubble"
                onClick={() => setActiveOptionMenuMessageId(prev => prev === (msg.id || msg.dbId) ? null : (msg.id || msg.dbId))}
              >
                {/* Reply Indicator Snippet */}
                {msg.replyTo && (
                  <div 
                    className="message-reply-snippet" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReplySnippetClick(msg.replyTo.id);
                    }}
                  >
                    <span className="reply-sender">{msg.replyTo.senderName}</span>
                    <span className="reply-text">{truncateText(msg.replyTo.text)}</span>
                  </div>
                )}

                {msg.mediaType === 'image' ? (
                  <img 
                    src={msg.text} 
                    className="chat-media-image" 
                    alt="رسالة صورة" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxImage(msg.text);
                    }} 
                    style={{ cursor: 'pointer', maxWidth: '200px', borderRadius: '12px', display: 'block', margin: '4px 0' }}
                  />
                ) : msg.mediaType === 'audio' ? (
                  <div className="audio-player-container" onClick={(e) => e.stopPropagation()}>
                    <audio controls src={msg.text} className="chat-media-audio" style={{ display: 'block', width: '100%', outline: 'none' }} />
                  </div>
                ) : (
                  <span className="message-text">{msg.text}</span>
                )}

                <div className="message-meta">
                  {msg.timestamp && (
                    <span className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {msg.sender === 'me' && renderMessageStatus(msg.status)}
                </div>

                {/* Bubble Context Option Menu overlay */}
                {activeOptionMenuMessageId === (msg.id || msg.dbId) && (
                  <div className="message-options-menu" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="option-item" onClick={() => {
                      const senderName = msg.sender === 'me' ? 'أنا' : (friend?.alias || friend?.firstName || 'صديقي');
                      setReplyingToMessage({
                        id: msg.id || msg.dbId,
                        text: msg.mediaType === 'image' ? '📷 صورة' : (msg.mediaType === 'audio' ? '🎙️ رسالة صوتية' : msg.text),
                        senderName
                      });
                      setActiveOptionMenuMessageId(null);
                    }}>
                      <CornerUpLeft size={16} /> الرد
                    </button>
                    {msg.sender === 'me' && (
                      <button type="button" className="option-item delete" onClick={() => {
                        handleDeleteMessage(msg.dbId || msg.id);
                        setActiveOptionMenuMessageId(null);
                      }}>
                        <Trash2 size={16} /> حذف
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          </>
        )}
        {isTyping && (
          <div className="direct-typing-indicator">
            <span></span><span></span><span></span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* حقل الإدخال / الحظر */}
      {isBlockedByMe ? (
        <div className="blocked-chat-overlay">
          <UserX size={28} className="blocked-icon" />
          <p>لقد قمت بحظر هذا المستخدم. قم بإلغاء الحظر لإرسال واستقبال الرسائل.</p>
          <button type="button" className="unblock-overlay-btn" onClick={handleUnblock}>إلغاء الحظر</button>
        </div>
      ) : (
        <div className="chat-input-wrapper-outer" style={{ position: 'relative', width: '100%' }}>
          
          {/* Active Reply Preview Bar */}
          {replyingToMessage && (
            <div className="chat-reply-preview-bar">
              <span className="reply-preview-icon"><CornerUpLeft size={16} /></span>
              <div className="reply-preview-content">
                <span className="reply-preview-sender">{replyingToMessage.senderName}</span>
                <span className="reply-preview-text">{truncateText(replyingToMessage.text)}</span>
              </div>
              <button 
                type="button" 
                className="reply-preview-close-btn" 
                onClick={() => setReplyingToMessage(null)}
              >
                <X size={16} />
              </button>
            </div>
          )}

          {showEmojiPicker && (
            <div className="quick-emoji-picker">
              {emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="emoji-btn"
                  onClick={() => handleEmojiClick(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {uploadingMedia && (
            <div className="chat-media-uploading-loader" style={{ position: 'absolute', top: '-40px', left: '0', right: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)', color: '#fff', padding: '8px', fontSize: '0.85rem', borderRadius: '10px', zIndex: 10 }}>
              <span className="loader-spinner-small" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }}></span>
              <span>جاري رفع وتجهيز الملف...</span>
            </div>
          )}

          {/* Hidden inputs */}
          <input
            type="file"
            accept="image/*"
            ref={photoInputRef}
            style={{ display: 'none' }}
            onChange={handlePhotoSelected}
          />

          {recordedAudioUrl && (
            <audio 
              ref={previewAudioRef} 
              src={recordedAudioUrl} 
              style={{ display: 'none' }} 
              onEnded={() => setIsPlayingPreview(false)}
            />
          )}

          {/* Voice Preview Player / Main Form toggle */}
          {recordedAudioUrl ? (
            <div className="voice-preview-container">
              <button type="button" className="voice-preview-btn discard" onClick={discardVoicePreview} title="حذف التسجيل">
                <Trash2 size={16} />
              </button>
              <div className="voice-preview-player-bar">
                <button type="button" className="voice-preview-play-btn" onClick={togglePlayPreview}>
                  {isPlayingPreview ? <Pause size={16} /> : <Play size={16} />}
                </button>
                <span className="voice-preview-label">
                  {isPlayingPreview ? "جاري الاستماع للرسالة الصوتية..." : "رسالة صوتية جاهزة"} ({formatDuration(recordingDuration)})
                </span>
              </div>
              <button type="button" className="voice-preview-btn send" onClick={sendVoicePreview} title="إرسال التسجيل">
                <Send size={16} />
              </button>
            </div>
          ) : isRecording ? (
            <div className="recording-status-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '14px', backdropFilter: 'blur(10px)', margin: '8px 0' }}>
              <div className="recording-details" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', fontWeight: 'bold' }}>
                <span className="recording-pulse-dot" style={{ width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite' }}></span>
                <span>تسجيل صوتي: {formatDuration(recordingDuration)}</span>
              </div>
              <div className="recording-actions-row" style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className="recording-action-btn cancel" onClick={() => stopVoiceRecording(false)} style={{ background: 'rgba(239, 68, 68, 0.2)', border: 'none', color: '#ef4444', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}><X size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '4px' }} /> إلغاء</button>
                <button type="button" className="recording-action-btn send" onClick={() => stopVoiceRecording(true)} style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}><Square size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '4px', fill: '#fff' }} /> إيقاف</button>
              </div>
            </div>
          ) : (
            <form className="direct-input-form" onSubmit={handleSend}>
              
              {/* Telegram-style input pill */}
              <div className="chat-input-pill">
                <button
                  type="button"
                  className={`emoji-toggle-btn ${showEmojiPicker ? 'active' : ''}`}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  title="إضافة إيموجي"
                >
                  <Smile size={22} />
                </button>

                <input
                  type="text"
                  placeholder="الرسالة..."
                  value={inputText}
                  onChange={handleInputChange}
                  dir="rtl"
                />

                {inputText.trim() === '' && (
                  <div className="attachment-wrapper-relative">
                    <button
                      type="button"
                      className="attachment-trigger-btn"
                      onClick={() => setShowAttachmentMenu(prev => !prev)}
                      title="إرفاق ملف"
                    >
                      <Paperclip size={22} />
                    </button>

                    {showAttachmentMenu && (
                      <div className="attachment-menu-popup">
                        <button
                          type="button"
                          className={`attachment-option ${!user?.isVip ? 'locked' : ''}`}
                          onClick={() => {
                            setShowAttachmentMenu(false);
                            handlePhotoClick();
                          }}
                        >
                          <span className="icon"><Camera size={18} /></span>
                          <span className="label">إرسال صورة</span>
                          {!user?.isVip && <span className="lock"><Lock size={12} /></span>}
                        </button>
                        <button
                          type="button"
                          className={`attachment-option ${!user?.isVip ? 'locked' : ''}`}
                          onClick={() => {
                            setShowAttachmentMenu(false);
                            startVoiceRecording();
                          }}
                        >
                          <span className="icon"><Mic size={18} /></span>
                          <span className="label">رسالة صوتية</span>
                          {!user?.isVip && <span className="lock"><Lock size={12} /></span>}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Circle Action Button */}
              {inputText.trim() === '' ? (
                <button
                  type="button"
                  className="chat-circle-action-btn mic"
                  onClick={startVoiceRecording}
                  title="سجل رسالة صوتية"
                >
                  <Mic size={20} />
                </button>
              ) : (
                <button
                  type="submit"
                  className="chat-circle-action-btn send"
                  title="إرسال"
                >
                  <Send size={20} />
                </button>
              )}
            </form>
          )}
        </div>
      )}

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSubmit={handleReport}
        entityName={friend?.alias || 'المستخدم'}
      />

      {lightboxImage && (
        <div className="image-lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <button className="lightbox-close-btn" onClick={() => setLightboxImage(null)}>×</button>
          <img src={lightboxImage} alt="عرض بالحجم الكامل" className="lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default DirectChat;
